/**
 * Auditor Dashboard Page
 *
 * Main landing page for auditors showing:
 * - Overview statistics (assigned applications, pending reviews, scheduled audits)
 * - Assigned applications queue
 * - Recent activity
 * - Upcoming audits
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Search, Calendar, CheckCircle2, FileText, Check, ScrollText } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { StatCard } from '../../components/dashboard/StatCard';
import { DashboardGrid } from '../../components/dashboard/DashboardGrid';
import { RecentActivityCard } from '../../components/dashboard/RecentActivityCard';
import { QuickActionsPanel } from '../../components/dashboard/QuickActionsPanel';
import { formatDate } from '../../utils/formatting';
import { mockApi } from '../../services/mockApi';
import type { Application } from '../../types/application';

export const AuditorDashboardPage: React.FC = () => {
  const { t } = useTranslation(['common', 'auditor']);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<Application[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const appsResponse = await mockApi.getAllApplications();
      if (appsResponse.success && appsResponse.data) {
        setApplications(appsResponse.data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const stats = {
    assignedApplications: applications.filter(a =>
      ['UNDER_REVIEW', 'AUDIT_SCHEDULED', 'AUDIT_IN_PROGRESS'].includes(a.status)
    ).length,
    pendingReviews: applications.filter(a =>
      a.status === 'UNDER_REVIEW'
    ).length,
    scheduledAudits: applications.filter(a =>
      a.status === 'AUDIT_SCHEDULED'
    ).length,
    completedThisMonth: applications.filter(a => {
      const completedDate = new Date(a.updatedAt);
      const now = new Date();
      return a.status === 'APPROVED' &&
             completedDate.getMonth() === now.getMonth() &&
             completedDate.getFullYear() === now.getFullYear();
    }).length,
  };

  // Get my queue (applications assigned to auditor)
  const myQueue = applications.filter(a =>
    ['UNDER_REVIEW', 'AUDIT_SCHEDULED', 'AUDIT_IN_PROGRESS'].includes(a.status)
  ).slice(0, 5);

  // Get recent activity (latest updated applications)
  const recentActivity = [...applications]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'AUDIT_SCHEDULED':
      case 'AUDIT_IN_PROGRESS':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'UNDER_REVIEW':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('auditor:dashboard.title')}
        footer={{ copyright: "© 2026 HALMAS. Araken Sdn. Bhd. All rights reserved." }}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('auditor:dashboard.title') },
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">{t('common:app.loading')}</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('auditor:dashboard.title')}
      footer={{ copyright: "© 2026 HALMAS. Araken Sdn. Bhd. All rights reserved." }}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('auditor:dashboard.title') },
      ]}
    >
      {/* Welcome Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('auditor:dashboard.welcome')}</h2>
        <p className="text-gray-600">{t('auditor:dashboard.overview')}</p>
      </div>

      {/* Statistics Cards */}
      <DashboardGrid cols={4} className="mb-8">
        <StatCard
          title={t('auditor:dashboard.assignedApplications')}
          value={stats.assignedApplications}
          icon={<ClipboardList className="w-6 h-6" />}
          variant="primary"
          subtitle="Active applications"
        />

        <StatCard
          title={t('auditor:dashboard.pendingReviews')}
          value={stats.pendingReviews}
          icon={<Search className="w-6 h-6" />}
          variant="warning"
          subtitle="Awaiting review"
        />

        <StatCard
          title={t('auditor:dashboard.scheduledAudits')}
          value={stats.scheduledAudits}
          icon={<Calendar className="w-6 h-6" />}
          variant="primary"
          subtitle="Upcoming audits"
        />

        <StatCard
          title={t('auditor:dashboard.completedThisMonth')}
          value={stats.completedThisMonth}
          icon={<CheckCircle2 className="w-6 h-6" />}
          variant="success"
          subtitle="This month"
          trend={{
            value: 12,
            direction: 'up',
            label: 'vs last month',
          }}
        />
      </DashboardGrid>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Application Queue */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t('auditor:dashboard.myQueue')}</h3>
              <Button variant="outline" onClick={() => window.location.href = '/auditor/applications'}>
                {t('auditor:dashboard.viewAllApplications')}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {myQueue.length === 0 ? (
              <p className="text-gray-500 text-center py-8">{t('auditor:applications.noApplications')}</p>
            ) : (
              <div className="space-y-4">
                {myQueue.map((application) => (
                  <div
                    key={application.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => window.location.href = `/auditor/applications/${application.id}`}
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{application.companyName}</h4>
                      <p className="text-sm text-gray-500">
                        {application.type} - {formatDate(application.submittedAt || application.createdAt)}
                      </p>
                    </div>
                    <Badge variant={getStatusVariant(application.status)}>
                      {application.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Recent Activity */}
        <RecentActivityCard
          title={t('auditor:dashboard.recentActivity')}
          items={recentActivity.map((application) => {
            const variant = getStatusVariant(application.status);
            return {
              id: application.id,
              icon: <FileText className="w-4 h-4" />,
              title: application.companyName,
              description: application.status.replace(/_/g, ' '),
              timestamp: new Date(application.updatedAt),
              variant: variant === 'info' ? 'primary' : variant,
              onClick: () => window.location.href = `/auditor/applications/${application.id}`,
            };
          })}
          maxItems={5}
          showViewAll
          onViewAllClick={() => window.location.href = '/auditor/applications'}
        />
      </div>

      {/* Quick Actions */}
      <QuickActionsPanel
        title="Quick Actions"
        layout="grid"
        className="mt-6"
        actions={[
          {
            id: 'applications',
            label: t('auditor:applications.applicationQueue'),
            icon: <ClipboardList className="w-4 h-4" />,
            onClick: () => window.location.href = '/auditor/applications',
            variant: 'outline',
          },
          {
            id: 'audits',
            label: t('auditor:auditChecklist.title'),
            icon: <Check className="w-4 h-4" />,
            onClick: () => window.location.href = '/auditor/audits',
            variant: 'outline',
          },
          {
            id: 'ncr',
            label: t('auditor:ncr.title'),
            icon: <FileText className="w-4 h-4" />,
            onClick: () => window.location.href = '/auditor/ncr',
            variant: 'outline',
          },
          {
            id: 'certificates',
            label: t('auditor:certificates.title'),
            icon: <ScrollText className="w-4 h-4" />,
            onClick: () => window.location.href = '/auditor/certificates',
            variant: 'outline',
          },
        ]}
      />
    </PageLayout>
  );
};

export default AuditorDashboardPage;
